import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import outdoorBasket from "../../assets/outdoor-basket.webp";

const title = "Leisure Activities | Swedish International School Estepona";
const description =
  "After-school care with Swedish-speaking educators in Estepona: sport, arts and crafts, music and drama in a safe environment until 4.30pm.";

export const Route = createFileRoute("/en/leisure-activities")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/leisure-activities") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/leisure-activities"),
  }),
  component: LeisureActivities,
});

function LeisureActivities() {
  return (
    <>
      <PageHero
        eyebrow="After the school day"
        title="Leisure activities"
        intro="After-school care (fritids) at Swedish International School is an important part of the school's activities, giving pupils the chance to take part in various activities and have fun together."
      />

      <Prose>
        <img
          src={outdoorBasket}
          alt="Pupils playing basketball in the school's outdoor area"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={810}
          height={540}
        />

        <h2>Activities for every interest</h2>
        <p>
          After-school care offers a wide range of activities for pupils to choose from. There are
          sports activities such as football, basketball and volleyball, creative activities such as
          drawing and painting, and music and drama activities such as theatre and singing. Pupils
          choose which activities they want to take part in, and there is something for every
          interest.
        </p>

        <h2>Community and friendship</h2>
        <p>
          The after-school activities are also an opportunity for pupils to get to know each other
          better and build strong relationships. Pupils come from different parts of Spain and the
          world, but all share the fact that they speak Swedish. The activities give pupils a chance
          to socialise with other Swedish-speaking children and form friendships.
        </p>

        <h2>New skills</h2>
        <p>
          Pupils get the chance to try out different activities and discover new interests they can
          continue to develop after school. For parents, after-school care means a safe and
          stimulating environment for children after the school day, with Swedish-speaking educators.
        </p>

        <h2>The rhythm of the afternoon</h2>
        <p>
          When the school day ends, the children gather with the staff before the afternoon gets going.
          There is time for a snack, for getting out into the yard and for quieter moments indoors for
          anyone who wants to wind down. After-school care is open until 16.30 on school days and
          follows the same{" "}
          <a className="text-primary link-quiet" href="/en/school-year-dates">school year dates</a>{" "}
          as the school, which means it is closed during holidays.
        </p>

        <h2>Good to bring</h2>
        <ul>
          <li>Clothes that can handle outdoor play and shoes for running.</li>
          <li>Sun protection and a cap during the warmer months.</li>
          <li>A water bottle labelled with your child's name.</li>
          <li>A change of clothes for the youngest children.</li>
        </ul>

        <h2>Contact during the afternoon</h2>
        <p>
          Let the school know if your child is to be collected by someone other than usual or is going
          home earlier than planned. For questions about places, times or fees, email{" "}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">info@sweintschool.com</a>.
          The cost is shown on the{" "}
          <a className="text-primary link-quiet" href="/en/fees">fees page</a> and is requested as
          part of the{" "}
          <a className="text-primary link-quiet" href="/en/application">application</a>.
        </p>
      </Prose>

      <FaqBlock items={faqFor("fritids", "en")} locale={"en"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
